import type {
  ProductAllocation,
  ProductGroup,
} from "@/marketing/models/influencerCampaign";

export const MAX_PRODUCT_GROUPS = 20;
export const MAX_PRODUCT_ALLOCATIONS = 50;

function productGroupLabel(productGroup: ProductGroup): string {
  return productGroup.name?.trim() || productGroup.groupId || "Unnamed group";
}

function duplicateValues(values: string[]): string[] {
  return values.filter(
    (value, index) => value !== "" && values.indexOf(value) !== index,
  );
}

export function createProductGroupId(productGroups: ProductGroup[]): string {
  const usedGroupIds = new Set(productGroups.map((productGroup) => productGroup.groupId));
  let groupNumber = 1;
  while (usedGroupIds.has(`group_${groupNumber}`)) {
    groupNumber += 1;
  }
  return `group_${groupNumber}`;
}

export function validateProductGroupConfiguration(
  productGroups: ProductGroup[],
  productAllocations: ProductAllocation[],
): string[] {
  const validationErrors: string[] = [];

  if (productGroups.length === 0) {
    validationErrors.push("Add at least one product group.");
  }
  if (productGroups.length > MAX_PRODUCT_GROUPS) {
    validationErrors.push(
      `A campaign can contain at most ${MAX_PRODUCT_GROUPS} product groups.`,
    );
  }
  if (productAllocations.length > MAX_PRODUCT_ALLOCATIONS) {
    validationErrors.push(
      `A campaign can contain at most ${MAX_PRODUCT_ALLOCATIONS} product options.`,
    );
  }

  const groupIds = productGroups.map((productGroup) => productGroup.groupId.trim());
  const duplicateGroupIds = new Set(duplicateValues(groupIds));
  for (const duplicateGroupId of duplicateGroupIds) {
    validationErrors.push(`Duplicate group ID "${duplicateGroupId}".`);
  }

  const allocationSkus = productAllocations.map((allocation) => allocation.sku.trim());
  const duplicateSkus = new Set(duplicateValues(allocationSkus));
  for (const duplicateSku of duplicateSkus) {
    validationErrors.push(`Duplicate SKU "${duplicateSku}".`);
  }

  const knownGroupIds = new Set(groupIds);
  for (const productAllocation of productAllocations) {
    const sku = productAllocation.sku.trim();
    if (!sku || sku.length > 64) {
      validationErrors.push(
        `Option SKU "${sku || "(blank)"}" must contain 1 to 64 characters.`,
      );
    }
    if (
      !Number.isInteger(productAllocation.quantity) ||
      productAllocation.quantity < 1 ||
      productAllocation.quantity > 100
    ) {
      validationErrors.push(
        `Option "${sku || "(blank)"}" quantity must be between 1 and 100.`,
      );
    }

    const allocationGroupId = productAllocation.groupId?.trim();
    if (!allocationGroupId) {
      validationErrors.push(`Option "${sku || "(blank)"}" must belong to a group.`);
    } else if (!knownGroupIds.has(allocationGroupId)) {
      validationErrors.push(
        `Option "${sku || "(blank)"}" references unknown group "${allocationGroupId}".`,
      );
    }
  }

  for (const productGroup of productGroups) {
    const groupId = productGroup.groupId.trim();
    const groupLabel = productGroupLabel(productGroup);
    if (!groupId || groupId.length > 64) {
      validationErrors.push(
        `Group ID "${groupId || "(blank)"}" must contain 1 to 64 characters.`,
      );
    }
    if ((productGroup.name?.length ?? 0) > 255) {
      validationErrors.push(`Group "${groupLabel}" name cannot exceed 255 characters.`);
    }
    if (
      !Number.isInteger(productGroup.choiceCount) ||
      productGroup.choiceCount < 1 ||
      productGroup.choiceCount > 50
    ) {
      validationErrors.push(
        `Group "${groupLabel}" choice count must be between 1 and 50.`,
      );
    }

    const selectableOptionCount = productAllocations.filter(
      (allocation) =>
        allocation.groupId === productGroup.groupId && !allocation.disabled,
    ).length;
    if (selectableOptionCount === 0) {
      validationErrors.push(
        `Group "${groupLabel}" needs at least one available option.`,
      );
    } else if (productGroup.choiceCount > selectableOptionCount) {
      validationErrors.push(
        `Group "${groupLabel}" requires ${productGroup.choiceCount} choices but only ${selectableOptionCount} ${selectableOptionCount === 1 ? "option is" : "options are"} available.`,
      );
    }
  }

  return validationErrors;
}
